import { JobSelector } from './JobSelector';
import { isDuplicateJob } from '../../lib/utils';
import { useJobHistory } from './hooks/useJobHistory';
import { FileUpload } from './FileUpload';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Briefcase } from 'lucide-react';
import { cn } from "@/lib/utils";

interface JobFormProps {
  jobTitle: string;
  jobDescription: string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  errors?: {
    title?: string;
    description?: string;
  };
  className?: string;
  isProjectContext?: boolean;
}

export function JobForm({
  jobTitle,
  jobDescription,
  onTitleChange,
  onDescriptionChange,
  errors = {},
  className,
  isProjectContext = false,
}: JobFormProps) {
  const { jobs = [] } = useJobHistory();

  const handleTitleChange = (value: string) => {
    if (isDuplicateJob(value, jobDescription, jobs)) { return; }
    onTitleChange(value);
  };

  const handleDescriptionChange = (value: string) => {
    if (isDuplicateJob(jobTitle, value, jobs)) { return; }
    onDescriptionChange(value);
  };

  return (
    <div className={cn("space-y-6 border bg-card text-card-foreground rounded-lg p-6 shadow-sm", className)}>
      <div className="flex items-center justify-between gap-4 pb-4 border-b">
        <div className="flex items-center space-x-3">
          <Briefcase className="h-5 w-5 text-primary flex-shrink-0" />
          <h3 className="text-lg font-semibold text-foreground">
            {isProjectContext ? 'Project Requirements' : 'Job Information'}
          </h3>
        </div>
        {!isProjectContext && (
          <JobSelector
            onSelect={({ title, description }) => {
              onTitleChange(title);
              onDescriptionChange(description);
            }}
          />
        )}
      </div>

      {isProjectContext && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          <p className="font-medium">📋 Project requirements have been automatically loaded</p>
          <p className="text-green-700">You can modify the job description if needed, or keep it as-is for consistent analysis.</p>
        </div>
      )}

      <div className="space-y-6 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="jobTitle">Job Title</Label>
          <Input
            id="jobTitle"
            value={jobTitle}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={isProjectContext ? "Project role title" : "Enter the job position title"}
            required
            aria-invalid={!!errors.title}
            aria-describedby={errors.title ? "jobTitle-error" : undefined}
          />
          {errors.title && (
            <p id="jobTitle-error" className="text-xs text-destructive pt-1">
              {errors.title}
            </p>
          )}
        </div>

        {!isProjectContext && (
          <FileUpload
            type="job"
            onFileSelect={() => {
              // If you need to handle the file itself, do so here.
            }}
            onTextExtracted={(text) => handleDescriptionChange(text)}
            onTitleExtracted={(title) => handleTitleChange(title)}
            label="Upload Job Description (PDF or Word Doc)"
          />
        )}

        <div className="space-y-1.5">
          <Label htmlFor="jobDescription">
            {isProjectContext ? 'Project Requirements & Description' : 'Job Description'}
          </Label>
          <Textarea
            id="jobDescription"
            value={jobDescription}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder={
              isProjectContext 
                ? "Requirements have been pre-filled from the project..." 
                : "Paste the job description here, or upload a file above..."
            }
            required
            rows={10}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? "jobDescription-error" : undefined}
          />
          {errors.description && (
            <p id="jobDescription-error" className="text-xs text-destructive pt-1">
              {errors.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
